import {FC} from 'react';
import {Box, Typography} from '@mui/material';
import {
    BoltOutlined,
    EmailOutlined,
    EvStationOutlined,
    GridViewOutlined,
    LocationOnOutlined,
    SvgIconComponent,
} from '@mui/icons-material';
import {FormattedMessage} from 'react-intl';
import {Link, useLocation} from 'react-router-dom';
import {useCanOpen} from 'src/permissions/hooks';
import {PAGE_PERMISSION} from 'src/permissions/pages';
import {useScreenTier} from 'src/hooks/useScreenTier';
import {AppRouteEnum, AppRouteSpace} from 'src/utils/helper';
import {phoneBottomItemSx} from 'src/components/mobile/mobileDesign.sx';
import {bottomNavSx} from './BottomNav.sx';

interface BottomNavItem {
    route: AppRouteEnum;
    labelId: string;
    icon: SvgIconComponent;
    allowed: boolean;
}

/**
 * The five places people go, fixed to the bottom of a phone.
 *
 * Everything else is one tap away on the More screen, which is why only five are here: the drawer is
 * a temporary sheet below `md` and a list nobody keeps open is not navigation.
 *
 * Each entry asks what its own page requires, reading the same {@link PAGE_PERMISSION} constant the
 * route guard and the drawer's entry read. A bar that offers a door the router will not open is
 * worse than a bar with four items.
 */
export const BottomNav: FC = () => {
    const {isCompact} = useScreenTier();
    const {pathname} = useLocation();
    // One call per entry, in a fixed order — hooks cannot be called behind a condition, so they are
    // all asked and the answers are used below.
    const can = {
        locations: useCanOpen(PAGE_PERMISSION[AppRouteEnum.locations]),
        chargers: useCanOpen(PAGE_PERMISSION[AppRouteEnum.chargers]),
        chargingSessions: useCanOpen(PAGE_PERMISSION[AppRouteEnum.chargingSessions]),
        invoices: useCanOpen(PAGE_PERMISSION[AppRouteEnum.invoices]),
    };
    // Which item is lit. The first segment after `/app` is the section someone is in, so a record's
    // page keeps its list's item coloured: `/app/locations/42` is still Locations.
    const section = pathname.split('/')[2] ?? '';

    const items: BottomNavItem[] = [
        {
            route: AppRouteEnum.locations,
            labelId: 'locations',
            icon: LocationOnOutlined,
            allowed: can.locations,
        },
        {
            route: AppRouteEnum.chargers,
            labelId: 'chargers',
            icon: EvStationOutlined,
            allowed: can.chargers,
        },
        {
            route: AppRouteEnum.chargingSessions,
            // "Sessions", not "Charging sessions": a bar item has about 74px on a 390px screen, and
            // the long key is 17 characters in English, 13 in German and 24 in French ("Les sessions
            // de recharge"). The short key already exists in all three and is the word the design
            // uses here - a label that fits beats a label that ellipses.
            labelId: 'sessions',
            icon: BoltOutlined,
            allowed: can.chargingSessions,
        },
        {
            route: AppRouteEnum.invoices,
            labelId: 'invoices',
            icon: EmailOutlined,
            allowed: can.invoices,
        },
        // The way to everything the bar cannot hold, so it is never hidden: without it a viewer whose
        // permissions hide the other four would have no navigation at all.
        {
            route: AppRouteEnum.more,
            labelId: 'more',
            icon: GridViewOutlined,
            allowed: true,
        },
    ];
    const visible = items.filter((item) => item.allowed);

    if (!isCompact) {
        return null;
    }

    return (
        <Box
            component="nav"
            sx={bottomNavSx(visible.length)}
        >
            {visible.map(({route, labelId, icon: Icon}) => (
                <Box
                    key={route}
                    component={Link}
                    to={AppRouteSpace([AppRouteEnum.app, route])}
                    sx={phoneBottomItemSx(route === section)}
                >
                    <Icon/>
                    <Typography>
                        <FormattedMessage id={labelId}/>
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};
